import { addMonths, addYears, startOfDay } from 'date-fns';
import { useCallback } from 'react';
import {
  Button,
  DatePicker,
  Flex,
  GridAlpha,
  Notice,
  NumberInput,
  Select,
  Text,
  createForm,
  useId,
  useToasts,
} from '@applyboard/crystal-ui';
import { ArrowRightOutlineIcon } from '@applyboard/ui-icons';
import { useEventTracker, useTranslation } from '@applyboard/ui-utils';
import { useGetSource, useGetUtmSource } from '../../../../common/hooks/useQueryParams';
import { zodValidateField } from '../../../../common/schema/forms';
import MandatoryFieldsHelpText from '../../MandatoryFieldsHelpText';
import {
  DATE_FIELDS_MAXIMUM_YEARS,
  EXPECTED_ARRIVAL_DATE_MINIMUM_MONTHS,
  EXPECTED_GRADUATION_DATE_MINIMUM_YEARS,
} from '../../constants';
import {
  estimatedLoanAmountSchema,
  expectedArrivalDateSchema,
  expectedGraduationDateSchema,
} from '../../formSchemas';
import { LoansFormEvents } from '../../types';
import { StepperState } from '../LoanFormStepper';
import { DestinationForm, SUPPORTED_LOAN_CURRENCIES_OPTIONS } from './types';

const { Field, Form } = createForm<DestinationForm>();

const DEFAULT_DESTINATION_VALUES: DestinationForm = {
  estimatedLoanAmount: '',
  loanAmountCurrency: '',
  expectedArrivalDate: '',
  expectedGraduationDate: '',
};

const Destination = ({ loanData, setLoanData, goNext }: StepperState) => {
  const formId = useId();
  const { t } = useTranslation();
  const toasts = useToasts();
  const utmSource = useGetUtmSource();
  const source = useGetSource();
  const { trackEvent } = useEventTracker();

  const defaultFormValues: DestinationForm =
    loanData.destinationDetails ?? DEFAULT_DESTINATION_VALUES;

  const submitCallback = useCallback(
    (values: DestinationForm) => {
      // validate loan data here

      setLoanData({
        ...loanData,
        destinationDetails: { ...values },
      });

      trackEvent({
        event: LoansFormEvents.CompletedDestination,
        data: { rp_id: loanData.recruitmentPartnerId, utm_source: utmSource, source: source },
      });

      goNext();
    },
    [goNext, loanData, setLoanData, source, trackEvent, utmSource],
  );

  return (
    <Form id={formId} defaultValues={defaultFormValues} onSubmit={submitCallback}>
      <GridAlpha columns="repeat(27, 1fr)" rowGap={6} pb={6}>
        <GridAlpha.Item columnStart={{ xs: 2 }} columnEnd={{ xs: 27 }}>
          <Notice intent="positive">
            <Flex p={0} align="center" direction="column">
              <Text contrast="high" intent="secondary" variant="bodyM" align="center">
                <strong>{t('loans.partialApplicationSubmitted.confirmationLine')}</strong>
              </Text>
              <Text contrast="high" intent="secondary" variant="bodyM" align="center">
                {t('loans.partialApplicationSubmitted.line1')}
              </Text>
              <Text contrast="high" intent="secondary" variant="bodyM" align="center">
                {t('loans.partialApplicationSubmitted.line2')}
              </Text>
            </Flex>
          </Notice>
        </GridAlpha.Item>

        <GridAlpha.Item columnStart={{ xs: 2, md: 2 }} columnEnd={{ xs: 27, md: 13 }}>
          {/* TODO: Address the layout issue due to help text:
          https://www.figma.com/file/U2ofUidRDr6qRyDC6hV0Ui?type=design&node-id=106-150306&mode=design#757914232 */}
          <Field
            as={DatePicker}
            name="expectedGraduationDate"
            label={t('loans.fieldLabels.expectedGraduationDate')}
            required={t('loans.errors.fieldRequired', {
              fieldName: t('loans.fieldLabels.expectedGraduationDate'),
            })}
            granularity="day"
            minDate={startOfDay(
              addYears(new Date(), EXPECTED_GRADUATION_DATE_MINIMUM_YEARS),
            ).toISOString()}
            maxDate={startOfDay(addYears(new Date(), DATE_FIELDS_MAXIMUM_YEARS)).toISOString()}
            size="md"
            // TODO: Fill in Help Text
            helpText={t('loans.fieldLabels.expectedGraduationDateHelpText')}
            validate={value => {
              return zodValidateField(
                expectedGraduationDateSchema(t),
                'expectedGraduationDate',
                value,
                toasts,
              );
            }}
          />
        </GridAlpha.Item>

        <GridAlpha.Item columnStart={{ xs: 2, md: 14 }} columnEnd={27}>
          <Field
            as={DatePicker}
            name="expectedArrivalDate"
            label={t('loans.fieldLabels.expectedArrivalDate')}
            required={t('loans.errors.fieldRequired', {
              fieldName: t('loans.fieldLabels.expectedArrivalDate'),
            })}
            granularity="day"
            minDate={startOfDay(
              addMonths(new Date(), EXPECTED_ARRIVAL_DATE_MINIMUM_MONTHS),
            ).toISOString()}
            maxDate={startOfDay(addYears(new Date(), DATE_FIELDS_MAXIMUM_YEARS)).toISOString()}
            size="md"
            helpText={t('loans.fieldLabels.expectedArrivalDateHelpText', {
              monthMinimum: EXPECTED_ARRIVAL_DATE_MINIMUM_MONTHS,
            })}
            validate={value => {
              return zodValidateField(
                expectedArrivalDateSchema(t),
                'expectedArrivalDate',
                value,
                toasts,
              );
            }}
          />
        </GridAlpha.Item>

        <GridAlpha.Item columnStart={{ xs: 2, md: 2 }} columnEnd={{ xs: 27, md: 9 }}>
          <Field
            as={NumberInput}
            name="estimatedLoanAmount"
            label={t('loans.fieldLabels.estimatedLoanAmount')}
            required={t('loans.errors.estimatedLoanAmountRequired')}
            validate={value => {
              return zodValidateField(
                estimatedLoanAmountSchema(t),
                'estimatedLoanAmount',
                value,
                toasts,
              );
            }}
          />
        </GridAlpha.Item>
        <GridAlpha.Item columnStart={{ xs: 2, md: 10 }} columnEnd={{ xs: 27, md: 13 }}>
          <Field
            appearance="native"
            as={Select}
            name="loanAmountCurrency"
            label={t('loans.fieldLabels.loanAmountCurrency')}
            required={t('loans.errors.fieldRequired', {
              fieldName: t('loans.fieldLabels.loanAmountCurrency'),
            })}
          >
            {SUPPORTED_LOAN_CURRENCIES_OPTIONS.map(opt => (
              <Select.Option {...opt} key={opt.value} />
            ))}
          </Field>
        </GridAlpha.Item>

        <GridAlpha.Item columnStart={{ xs: 2, md: 2 }} columnEnd={27}>
          <MandatoryFieldsHelpText />
        </GridAlpha.Item>
        <GridAlpha.Item columnStart={14} columnEnd={27}>
          <Flex justify="end" pt={8}>
            <Button
              intent="primary"
              size="lg"
              width="hug"
              trailIcon={ArrowRightOutlineIcon}
              type="submit"
              form={formId}
            >
              {t('loans.continue')}
            </Button>
          </Flex>
        </GridAlpha.Item>
      </GridAlpha>
    </Form>
  );
};

export default Destination;
